/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { toET } from '../utils/et';
import { format } from 'date-fns';

function Bubble({ from, text, at }) {
  const isLgh = from === 'lgh';
  return (
    <div style={{ display: 'flex', justifyContent: isLgh ? 'flex-start' : 'flex-end', marginBottom: 8 }}>
      <div style={{
        maxWidth: '80%', padding: '10px 14px', borderRadius: 16,
        background: isLgh ? '#f0f0f0' : '#185FA5',
        color: isLgh ? '#333' : 'white',
        fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-wrap',
        borderBottomLeftRadius: isLgh ? 4 : 16,
        borderBottomRightRadius: isLgh ? 16 : 4,
      }}>
        {text}
        {at && <div style={{ fontSize: 9, opacity: 0.6, marginTop: 4, textAlign: isLgh ? 'left' : 'right' }}>
          {format(toET(at), 'h:mm a')}
        </div>}
      </div>
    </div>
  );
}

export default function LghChat() {
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [newSessionAlert, setNewSessionAlert] = useState(false);
  const scrollRef = useRef(null);
  const selectedIdRef = useRef(null);
  const sessionCountRef = useRef(0);
  const userScrolledRef = useRef(false);

  // Keep ref in sync so interval callbacks see latest value
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  const loadSession = async (id) => {
    setSelectedId(id);
    try {
      const res = await api.get(`/lgh-bot/sessions/${id}`);
      const s = res.data.session;
      const ctx = typeof s.context === 'string' ? JSON.parse(s.context) : s.context;
      setTranscript(ctx?.transcript || []);
      setSessionInfo(s);
    } catch (e) {}
  };

  const loadSessions = async () => {
    try {
      const res = await api.get('/lgh-bot/sessions');
      const list = res.data.sessions || [];
      // Alert on new sessions (after initial load)
      if (sessionCountRef.current > 0 && list.length > sessionCountRef.current) {
        setNewSessionAlert(true);
        // Play a subtle notification sound if available
        try { new Audio('data:audio/wav;base64,UklGRl9vT19teleXBldGFtYXRhAAAA').play().catch(() => {}); } catch(e) {}
      }
      sessionCountRef.current = list.length;
      setSessions(list);
      // Auto-select the most recent if none selected
      if (!selectedIdRef.current && list.length > 0) {
        loadSession(list[0].id);
      }
      // Refresh selected session transcript
      if (selectedIdRef.current) {
        const sel = list.find(s => s.id === selectedIdRef.current);
        if (sel) {
          const ctx = typeof sel.context === 'string' ? JSON.parse(sel.context) : sel.context;
          const newTranscript = ctx?.transcript || [];
          setTranscript(prev => {
            if (newTranscript.length !== prev.length) return newTranscript;
            return prev;
          });
        }
      }
    } catch (e) {}
  };

  // Single stable interval — no stacking
  useEffect(() => {
    loadSessions();
    const interval = setInterval(loadSessions, 5000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Smart auto-scroll: only scroll to bottom if user hasn't scrolled up
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    userScrolledRef.current = !atBottom;
  };

  useEffect(() => {
    if (scrollRef.current && !userScrolledRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript]);

  const liveUrl = `${window.location.origin}/api/lgh-chat/live`;

  return (
    <div style={{ padding: '12px 0', display: 'flex', gap: 14, height: 'calc(100vh - 80px)' }}>
      {/* Session list */}
      <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h1 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>LGH Chat</h1>
          <a href={liveUrl} target="_blank" rel="noreferrer" style={{
            fontSize: 10, padding: '4px 8px', background: '#E6F1FB', color: '#185FA5',
            borderRadius: 4, textDecoration: 'none', fontWeight: 500,
          }}>Chat link</a>
        </div>

        {newSessionAlert && (
          <div onClick={() => { setNewSessionAlert(false); if (sessions.length > 0) loadSession(sessions[0].id); }}
            style={{ padding: '8px 12px', margin: '0 0 8px', background: '#FFF3E0', border: '1px solid #FFB74D',
              borderRadius: 8, cursor: 'pointer', fontSize: 12, color: '#BA7517', fontWeight: 500 }}>
            New LGH conversation received — click to view
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', background: 'white', border: '0.5px solid #e0e0db', borderRadius: 10 }}>
          {sessions.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 12 }}>No conversations yet</div>
          )}
          {sessions.map(s => {
            const ctx = typeof s.context === 'string' ? JSON.parse(s.context) : s.context;
            const dest = ctx?.slots?.destination_name || '';
            const patient = ctx?.slots?.patient_last_name || '';
            const isActive = s.id === selectedId;
            const msgCount = (ctx?.transcript || []).length;
            return (
              <div key={s.id} onClick={() => loadSession(s.id)} style={{
                padding: '10px 12px', cursor: 'pointer', borderBottom: '0.5px solid #f0f0f0',
                background: isActive ? '#E6F1FB' : 'white',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? '#185FA5' : '#333' }}>
                    {dest || patient || 'New request'}
                  </span>
                  <span style={{ fontSize: 10, color: '#999' }}>{format(toET(s.updated_at), 'h:mm a')}</span>
                </div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                  {patient && dest ? `${patient} → ${dest}` : s.state}
                  {s.transport_number && <span style={{ marginLeft: 4, color: '#185FA5' }}>{s.transport_number}</span>}
                  <span style={{ marginLeft: 4 }}>{msgCount} msg{msgCount !== 1 ? 's' : ''}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Transcript */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {sessionInfo && (
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 12, color: '#888' }}>
              {sessionInfo.state === 'confirmed' && <span style={{ color: '#0F6E56', fontWeight: 600 }}>Confirmed</span>}
              {sessionInfo.state === 'collecting' && <span style={{ color: '#BA7517', fontWeight: 600 }}>In progress</span>}
              {sessionInfo.state === 'idle' && <span style={{ color: '#999' }}>Idle</span>}
              {sessionInfo.transport_number && <span style={{ marginLeft: 8, color: '#185FA5', fontWeight: 500 }}>{sessionInfo.transport_number}</span>}
            </div>
          </div>
        )}

        <div ref={scrollRef} onScroll={handleScroll} style={{
          flex: 1, background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12,
          padding: '16px 18px', overflowY: 'auto',
        }}>
          {transcript.length === 0 ? (
            <div style={{ color: '#999', fontSize: 13, textAlign: 'center', padding: '3rem' }}>
              {selectedId ? 'No messages in this session' : 'Select a conversation from the left'}
            </div>
          ) : (
            transcript.map((t, i) => <Bubble key={i} from={t.from} text={t.text} at={t.at} />)
          )}
        </div>
      </div>
    </div>
  );
}
